/**
 * useAIChat Hook
 * Manages the state and logic for the AI chat conversation.
 * Handles message history, tool calls, confirmation flow, and lesson storage.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/Storage';
import {
  aiProvider,
  assembleSystemPrompt,
  allTools,
  toolRequiresConfirmation,
  createToolHandlers,
  createActionHandlers,
  searchKnowledge,
  type ChatMessage,
  type ToolCall,
  type ToolResult,
} from '../ai';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessageDisplay {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  isLoading?: boolean;
  isStreaming?: boolean;
  toolCalls?: ToolCall[];
  toolResults?: ToolResultDisplay[];
  timestamp: Date;
}

export interface ToolResultDisplay {
  toolName: string;
  result: ToolResult;
}

export interface PendingConfirmation {
  id: string;
  description: string;
  toolCall: ToolCall;
  humanReadableAction: string;
  precedingResults?: ToolResultDisplay[]; // Results from non-confirmation tools before this one
}

export interface Lesson {
  id: string;
  topic: string;
  content: string;
  createdAt: string;
  lastAccessed: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_LESSONS_KEY = 'balancio_ai_lessons';
const STORAGE_MESSAGES_KEY = 'balancio_ai_messages';
const MAX_HISTORY_MESSAGES = 50;

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAIChat() {
  const navigate = useNavigate();
  const appContext = useAppContext();
  const { token, user } = appContext;

  // Load messages from localStorage on initialization
  const [messages, setMessages] = useState<ChatMessageDisplay[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_MESSAGES_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore timestamps as Date objects
        return parsed.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
      }
    } catch { }
    return [];
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);

  // Refs for non-reactive access
  const messagesRef = useRef<ChatMessageDisplay[]>([]);
  const processingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem(STORAGE_MESSAGES_KEY, JSON.stringify(messages));
      } else {
        localStorage.removeItem(STORAGE_MESSAGES_KEY);
      }
    } catch { }
  }, [messages]);

  useEffect(() => {
    processingRef.current = isProcessing;
  }, [isProcessing]);

  // ============ Lessons Management ============

  const getStoredLessons = useCallback((): Lesson[] => {
    try {
      const stored = localStorage.getItem(STORAGE_LESSONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const storeLesson = useCallback((topic: string, content: string): void => {
    const lessons = getStoredLessons();
    const newLesson: Lesson = {
      id: generateId(),
      topic,
      content,
      createdAt: new Date().toISOString(),
      lastAccessed: new Date().toISOString(),
    };
    lessons.push(newLesson);
    localStorage.setItem(STORAGE_LESSONS_KEY, JSON.stringify(lessons));
  }, [getStoredLessons]);

  const retrieveLessons = useCallback((query: string): Lesson[] => {
    const lessons = getStoredLessons();
    const searchTerms = query.toLowerCase().split(/\s+/).filter(t => t.length > 2);

    return lessons.filter(lesson => {
      const searchText = `${lesson.topic} ${lesson.content}`.toLowerCase();
      return searchTerms.some(term => searchText.includes(term));
    }).slice(0, 5);
  }, [getStoredLessons]);

  const getLessonsContextString = useCallback((): string => {
    const lessons = getStoredLessons();
    if (lessons.length === 0) return '';
    return lessons.map(l => `- [${l.topic}]: ${l.content}`).join('\n');
  }, [getStoredLessons]);

  // ============ Core Chat Logic ============

  const addMessage = useCallback((message: ChatMessageDisplay) => {
    setMessages(prev => {
      const updated = [...prev, message];
      if (updated.length > MAX_HISTORY_MESSAGES) {
        return updated.slice(updated.length - MAX_HISTORY_MESSAGES);
      }
      return updated;
    });
  }, []);

  const updateLastMessage = useCallback((updates: Partial<ChatMessageDisplay>) => {
    setMessages(prev => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (lastIndex >= 0) {
        updated[lastIndex] = { ...updated[lastIndex], ...updates };
      }
      return updated;
    });
  }, []);

  /**
   * Build chat history for the API request.
   * 
   * IMPORTANT: To avoid DeepSeek's "tool messages responding to each tool_call_id" error,
   * we track which tool_call_ids have already been added to the history.
   * When extraResults are passed, we skip any tool results that would duplicate an
   * existing message in the conversation history.
   * 
   * Optionally accepts extra tool results to append (for the sendToolResultsToAI flow).
   */
  const buildChatHistory = useCallback((
    extraResults?: { toolName: string; toolCallId: string; result: ToolResult }[]
  ): ChatMessage[] => {
    const history: ChatMessage[] = [];

    // Add system prompt
    const currentPage = window.location.pathname;
    const lessonsContext = getLessonsContextString();

    history.push({
      role: 'system',
      content: assembleSystemPrompt({
        userName: user?.username || user?.firstName || undefined,
        currentPage,
        lessonsContext: lessonsContext || undefined,
      }),
    });

    // Track tool_call_ids we've already added to avoid duplicates
    const addedToolCallIds = new Set<string>();

    // Add conversation history
    for (const msg of messagesRef.current) {
      if (msg.role === 'user') {
        history.push({ role: 'user', content: msg.content });
      } else if (msg.role === 'assistant') {
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: msg.content,
        };

        // Only include tool_calls that have matching results, OR if all tool_calls
        // have results. If partial results exist (e.g., user confirmed only some actions),
        // only include those tool_calls that have results to avoid protocol errors.
        if (msg.toolCalls && msg.toolCalls.length > 0) {
          if (msg.toolResults && msg.toolResults.length > 0) {
            // We have results - only include tool_calls that have matching results
            const resultToolCallIds = new Set(
              msg.toolResults.map((_, i) => msg.toolCalls![i]?.id).filter(Boolean)
            );
            const matchedToolCalls = msg.toolCalls.filter(tc => resultToolCallIds.has(tc.id));
            if (matchedToolCalls.length > 0) {
              assistantMsg.tool_calls = matchedToolCalls;
            }
            // If no matching tool_calls, don't add any (to avoid bare assistant msg with no content)
            if (!assistantMsg.content && !assistantMsg.tool_calls?.length) {
              continue;
            }
          } else {
            // No results for these tool_calls yet - include them as-is
            assistantMsg.tool_calls = msg.toolCalls;
          }
        }

        if (assistantMsg.content || assistantMsg.tool_calls?.length) {
          history.push(assistantMsg);
        }

        // If there were tool results, add them using the correct tool_call_id
        if (msg.toolResults && msg.toolResults.length > 0 && msg.toolCalls) {
          for (let i = 0; i < msg.toolResults.length; i++) {
            const tr = msg.toolResults[i];
            const tcId = msg.toolCalls[i]?.id || tr.toolName;
            if (!addedToolCallIds.has(tcId)) {
              addedToolCallIds.add(tcId);
              history.push({
                role: 'tool',
                content: JSON.stringify(tr.result),
                tool_call_id: tcId,
              });
            }
          }
        }
      }
    }

    // If extra results need to be appended (from a pending confirmation follow-up),
    // add the assistant message with tool_calls and then the tool results.
    // Only add tool results whose tool_call_ids have NOT already been added.
    if (extraResults && extraResults.length > 0) {
      // Filter out extra results whose tool_call_ids are already in history
      const neededResults = extraResults.filter(er => !addedToolCallIds.has(er.toolCallId));

      if (neededResults.length > 0) {
        // Check if the latest assistant message already has matching tool_calls
        const lastAssistantIndex = [...history].reverse().findIndex(msg => msg.role === 'assistant');
        const lastAssistant = lastAssistantIndex >= 0
          ? history[history.length - 1 - lastAssistantIndex]
          : null;

        const firstNeededId = neededResults[0].toolCallId;
        const hasMatchingAssistant = lastAssistant?.tool_calls?.some(
          tc => tc.id === firstNeededId
        );

        if (!hasMatchingAssistant) {
          // Create a synthetic assistant message to satisfy the protocol
          history.push({
            role: 'assistant',
            content: '',
            tool_calls: neededResults.map(er => ({
              id: er.toolCallId,
              type: 'function' as const,
              function: { name: er.toolName, arguments: '{}' },
            })),
          });
        }

        for (const er of neededResults) {
          history.push({
            role: 'tool',
            content: JSON.stringify(er.result),
            tool_call_id: er.toolCallId,
          });
        }
      }
    }

    return history;
  }, [user, getLessonsContextString]);

  /**
   * Execute a tool call and return the result
   */
  const executeTool = useCallback(async (toolCall: ToolCall): Promise<ToolResult> => {
    const toolName = toolCall.function.name;
    const args = toolCall.function.arguments;

    // Handle knowledge tools directly (don't need API calls)
    if (toolName === 'store_lesson') {
      try {
        const { topic, content } = JSON.parse(args);
        storeLesson(topic, content);
        return {
          success: true,
          data: null,
          formattedMessage: `Lesson "${topic}" stored successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'retrieve_lessons') {
      try {
        const { query } = JSON.parse(args);
        const lessons = retrieveLessons(query);
        return {
          success: true,
          data: lessons,
          formattedMessage: lessons.length > 0
            ? `Found ${lessons.length} relevant lesson(s):\n${lessons.map((l: any) => `- [${l.topic}]: ${l.content}`).join('\n')}`
            : 'No relevant lessons found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'navigate_to_page') {
      try {
        const { path, page_name } = JSON.parse(args);
        navigate(path);
        return {
          success: true,
          data: { path, page_name },
          formattedMessage: `Navigating to ${page_name}...`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'go_to_profile') {
      try {
        navigate('/profile');
        return {
          success: true,
          data: { path: '/profile', page_name: 'Profile Settings' },
          formattedMessage: `Navigating to your Profile/Settings page...`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'toggle_dark_mode') {
      return {
        success: true,
        data: null,
        formattedMessage: `You can toggle dark mode by clicking the theme switch in the sidebar on the left side of the screen.`,
      };
    }

    // Handle refresh_page tool - reload current page so user sees updated data
    if (toolName === 'refresh_page') {
      try {
        const { message } = JSON.parse(args);
        // Use window.location.reload() which does a full page reload
        // to ensure all React Query data and components re-fetch
        setTimeout(() => {
          window.location.reload();
        }, 500);
        return {
          success: true,
          data: null,
          formattedMessage: message || 'Page refreshed.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    // Handle stop_chain tool - returns a signal to stop further AI processing
    if (toolName === 'stop_chain') {
      try {
        const { message } = JSON.parse(args);
        return {
          success: true,
          data: { __stopChain: true },
          formattedMessage: message || '',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    // Handle search knowledge tool
    if (toolName === 'answer_from_knowledge_base') {
      try {
        const { query } = JSON.parse(args);
        const results = searchKnowledge(query);
        return {
          success: true,
          data: results,
          formattedMessage: results.length > 0
            ? results.map(r => `**${r.topic}**: ${r.content}`).join('\n\n---\n\n')
            : 'I could not find specific information about that in my knowledge base.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    // For all other tools, use the service handlers
    try {
      // Check if it's an action tool (needs handler from actionHandlers)

      if (toolRequiresConfirmation(toolName)) {
        const actionHandlers = createActionHandlers(appContext);
        const actionHandler = (actionHandlers as any)[toolName];
        if (actionHandler) {
          return await actionHandler(args);
        }
      }

      // Otherwise use the read tool handlers
      const handlers = createToolHandlers(appContext);
      const handler = (handlers as any)[toolName];
      if (!handler) {
        throw new Error(`Unknown tool: ${toolName}`);
      }
      return await handler(args);
    } catch (error: any) {
      return { success: false, data: null, error: error.message };
    }
  }, [navigate, storeLesson, retrieveLessons, appContext]);

  /**
   * Process all tool calls from the AI response sequentially
   */
  const processToolCalls = useCallback(async (
    toolCalls: ToolCall[]
  ): Promise<{ results: ToolResultDisplay[]; needsConfirmation: PendingConfirmation | null }> => {
    const results: ToolResultDisplay[] = [];
    let confirmation: PendingConfirmation | null = null;

    for (const toolCall of toolCalls) {
      const toolName = toolCall.function.name;

      // Check if this tool requires user confirmation
      if (toolRequiresConfirmation(toolName)) {
        // Generate a human-readable description
        let description = `I'd like to perform the following action: **${toolName}**`;
        try {
          const args = JSON.parse(toolCall.function.arguments);
          const argsDescription = Object.entries(args)
            .map(([key, value]) => `- ${key}: ${value}`)
            .join('\n');
          description = `**Action**: ${toolName}\n\n**Details**:\n${argsDescription}`;
        } catch {
          // Use default description
        }

        confirmation = {
          id: generateId(),
          description,
          toolCall,
          humanReadableAction: description,
          precedingResults: results.length > 0 ? [...results] : undefined,
        };
        // Stop processing more tools - we need user confirmation first
        break;
      }

      // Execute immediately for non-confirmation tools
      const result = await executeTool(toolCall);
      results.push({ toolName, result });
    }

    return { results, needsConfirmation: confirmation };
  }, [executeTool]);

  /**
   * Send tool results back to the AI for a final response
   * Uses the original assistant message (before toolResults were added) to build
   * a clean history that matches DeepSeek's required tool_call protocol.
   */
  const sendToolResultsToAI = useCallback(async (
    results: ToolResultDisplay[],
    originalAssistantMsg: ChatMessageDisplay
  ): Promise<ChatMessageDisplay> => {
    // Build the extra results with their correct tool_call_ids
    const extraResults = results.map((tr, i) => ({
      toolName: tr.toolName,
      toolCallId: originalAssistantMsg.toolCalls?.[i]?.id || tr.toolName,
      result: tr.result,
    }));

    // Add tool results to the assistant message in state
    // BUT preserve the original assistant message WITHOUT toolResults for history building
    const updatedAssistantMsg: ChatMessageDisplay = {
      ...originalAssistantMsg,
      toolResults: results,
    };

    // Update messages state immediately
    setMessages(prev => {
      const updated = [...prev];
      const idx = updated.findIndex(m => m.id === originalAssistantMsg.id);
      if (idx >= 0) {
        updated[idx] = updatedAssistantMsg;
      }
      return updated;
    });

    // Build history. Since we've already updated messagesRef (via state update -> useEffect),
    // we buildChatHistory will find the message WITH toolResults. To avoid double-sending,
    // construct the history manually: pass the extraResults which represent the tool messages
    // that need to be added after the assistant message with tool_calls.
    const history = buildChatHistory(extraResults);

    // Get AI's final response
    let response;
    try {
      response = await aiProvider.chat({
        messages: history,
        tools: allTools,
      }, token || undefined);
    } catch (error: any) {
      // === AUTOMATIC RECOVERY: Try to recover from ANY API error ===
      // This handles tool protocol mismatches, invalid schema errors, etc.
      // by rebuilding history WITHOUT tool results and retrying.
      try {
        // Rebuild history without the extra tool results
        const recoveryHistory = buildChatHistory();
        // Add just the user's latest intent as a summary
        recoveryHistory.push({
          role: 'user',
          content: 'Please continue with the result of the action that was just completed.'
        });
        response = await aiProvider.chat({
          messages: recoveryHistory,
          tools: allTools,
        }, token || undefined);
      } catch (recoveryError: any) {
        // If recovery also fails, throw the original error
        throw error;
      }
    }

    const responseMsg = response.choices[0]?.message;

    const finalMessage: ChatMessageDisplay = {
      id: generateId(),
      role: 'assistant',
      content: responseMsg.content || '',
      timestamp: new Date(),
      toolCalls: responseMsg.tool_calls,
    };

    setMessages(prev => [...prev, finalMessage]);

    // If the response has more tool calls, process them recursively
    if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
      await handleToolCallsInMessage(finalMessage);
    }

    return finalMessage;
  }, [buildChatHistory, token]);

  /**
   * Handle tool calls in a message - processes them and potentially waits for confirmation
   * If the results include a stop_chain signal, do NOT send results back to AI.
   * Includes automatic recovery for API protocol errors.
   */
  const handleToolCallsInMessage = useCallback(async (message: ChatMessageDisplay) => {
    if (!message.toolCalls || message.toolCalls.length === 0) return;

    setIsProcessing(true);

    try {
      const { results, needsConfirmation } = await processToolCalls(message.toolCalls);

      if (needsConfirmation) {
        // Set pending confirmation and wait for user action
        setPendingConfirmation(needsConfirmation);

        // Update message to indicate waiting for confirmation
        updateLastMessage({
          content: message.content + '\n\n*Waiting for your confirmation...*',
          toolResults: results,
        });
      } else {
        // Check if any result has the stop_chain signal
        const hasStopChain = results.some(
          r => r.result?.data && (r.result.data as any).__stopChain === true
        );

        if (hasStopChain) {
          // Update message with tool results but DO NOT send back to AI
          updateLastMessage({
            toolResults: results,
          });
        } else {
          // All tools executed, send results back to AI for final response
          try {
            await sendToolResultsToAI(results, message);
          } catch (sendError: any) {
            // === AUTOMATIC RECOVERY ===
            // If sending to AI fails (e.g., protocol error), show the tool results
            // to the user and let them know the AI response failed
            updateLastMessage({
              content: message.content,
              toolResults: results,
            });
            // Add a recovery message from the assistant
            const recoveryMsg: ChatMessageDisplay = {
              id: generateId(),
              role: 'assistant',
              content: `The action was completed successfully, but I encountered an issue generating a follow-up response. The tool results are shown above. What would you like to do next?`,
              timestamp: new Date(),
            };
            setMessages(prev => [...prev, recoveryMsg]);
          }
        }
      }
    } catch (error: any) {
      setError(error.message);
      updateLastMessage({
        content: message.content + `\n\nError executing action: ${error.message}`,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [processToolCalls, sendToolResultsToAI, updateLastMessage]);

  /**
   * Confirm and execute a pending action
   */
  const confirmAction = useCallback(async () => {
    if (!pendingConfirmation) return;

    const { toolCall, precedingResults } = pendingConfirmation;
    setPendingConfirmation(null);
    setIsProcessing(true);

    try {
      // Execute the confirmed tool
      const result = await executeTool(toolCall);
      const results: ToolResultDisplay[] = [
        ...(precedingResults || []),
        { toolName: toolCall.function.name, result },
      ];

      // Update the last assistant message
      const lastMsg = messagesRef.current[messagesRef.current.length - 1];
      if (lastMsg) {
        // Check if the result has stop_chain signal (e.g., from refresh_page which reloads)
        const hasStopChain = result?.data && (result.data as any).__stopChain === true;

        if (hasStopChain) {
          // Just update with tool results, don't send back to AI
          updateLastMessage({
            toolResults: results,
          });
        } else {
          // Send tool results to AI for final response
          await sendToolResultsToAI(results, lastMsg);
        }
      }
    } catch (error: any) {
      setError(error.message);
      updateLastMessage({
        content: `Error executing action: ${error.message}`,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [pendingConfirmation, executeTool, sendToolResultsToAI, updateLastMessage]);

  /**
   * Cancel a pending action
   */
  const cancelAction = useCallback(() => {
    setPendingConfirmation(null);

    // Tell the AI that the user cancelled
    const cancelMsg: ChatMessageDisplay = {
      id: generateId(),
      role: 'assistant',
      content: 'The action was cancelled by the user. Is there anything else I can help you with?',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, cancelMsg]);
    setIsProcessing(false);
  }, []);

  /**
   * Send a user message and get AI response
   */
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isProcessing) return;

    setError(null);

    // Add user message
    const userMsg: ChatMessageDisplay = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);

    setIsProcessing(true);

    try {
      // Build chat history
      const history = buildChatHistory();
      history.push({ role: 'user', content: content.trim() });

      // Call AI through backend proxy
      const response = await aiProvider.chat({
        messages: history,
        tools: allTools,
      }, token || undefined);

      const responseMsg = response.choices[0]?.message;

      const assistantMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: responseMsg.content || '',
        timestamp: new Date(),
        toolCalls: responseMsg.tool_calls,
      };

      setMessages(prev => [...prev, assistantMsg]);

      // If the AI wants to call tools, handle them
      if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
        try {
          await handleToolCallsInMessage(assistantMsg);
        } catch (toolError: any) {
          // === AUTOMATIC RECOVERY ===
          // If tool handling failed, show a recovery message to the user
          const recoveryMsg: ChatMessageDisplay = {
            id: generateId(),
            role: 'assistant',
            content: `I encountered an issue while processing the action (${toolError.message}). Some results may have been processed. Please try again or let me know how I can help.`,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, recoveryMsg]);
        }
      }
    } catch (error: any) {
      // === AUTOMATIC RECOVERY: For API errors, try once more with a clean
      // history (tools only, no conversation baggage) ===
      if (error.message && !error.message.includes('not configured') && !error.message.includes('Authentication')) {
        try {
          const recoveryHistory: ChatMessage[] = [];
          const currentPage = window.location.pathname;
          const lessonsContext = getLessonsContextString();
          recoveryHistory.push({
            role: 'system',
            content: assembleSystemPrompt({
              userName: user?.username || user?.firstName || undefined,
              currentPage,
              lessonsContext: lessonsContext || undefined,
            }),
          });
          recoveryHistory.push({
            role: 'user',
            content: content.trim() + ' (Note: A previous attempt failed due to: ' + error.message + ')'
          });

          const recoveryResponse = await aiProvider.chat({
            messages: recoveryHistory,
            tools: allTools,
          }, token || undefined);

          const recoveryMsg = recoveryResponse.choices[0]?.message;
          const assistantMsg: ChatMessageDisplay = {
            id: generateId(),
            role: 'assistant',
            content: recoveryMsg.content || '',
            timestamp: new Date(),
            toolCalls: recoveryMsg.tool_calls,
          };
          setMessages(prev => [...prev, assistantMsg]);

          if (recoveryMsg.tool_calls && recoveryMsg.tool_calls.length > 0) {
            try {
              await handleToolCallsInMessage(assistantMsg);
            } catch (toolError: any) {
              const recoveryMsg2: ChatMessageDisplay = {
                id: generateId(),
                role: 'assistant',
                content: `I encountered an issue while processing the action (${toolError.message}). Some results may have been processed. Please try again or let me know how I can help.`,
                timestamp: new Date(),
              };
              setMessages(prev => [...prev, recoveryMsg2]);
            }
          }
          return; // Recovery succeeded, skip the error path
        } catch (recoveryError: any) {
          // Recovery also failed, fall through to error path
        }
      }

      const errorMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: `I apologize, but I encountered an error: ${error.message}\n\nPlease try again or rephrase your question. If the issue persists, check that everything is configured correctly.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, buildChatHistory, handleToolCallsInMessage, token, user, getLessonsContextString]);

  /**
   * Clear the chat history (and remove persisted messages)
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setPendingConfirmation(null);
    setError(null);
    setEditingMessageId(null);
    try {
      localStorage.removeItem(STORAGE_MESSAGES_KEY);
    } catch {}
  }, []);

  /**
   * Start editing a user message.
   * When a user message is edited, ALL messages after it are removed,
   * and the user can send a new message to continue the conversation from that point.
   */
  const startEditingMessage = useCallback((messageId: string) => {
    setEditingMessageId(messageId);
  }, []);

  /**
   * Cancel editing and restore the original messages
   */
  const cancelEditing = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  /**
   * Replace all messages from a given point onwards.
   * Updates the user's message with new content and removes all subsequent messages.
   * Then automatically sends the edited message.
   */
  const editMessageAndContinue = useCallback(async (messageId: string, newContent: string) => {
    if (!newContent.trim() || isProcessing) return;

    setEditingMessageId(null);

    // Find the message index
    const currentMessages = messagesRef.current;
    const msgIndex = currentMessages.findIndex(m => m.id === messageId);
    if (msgIndex < 0) return;

    // Keep messages up to (and including) the edited message
    const keptMessages = currentMessages.slice(0, msgIndex + 1);
    
    // Update the user's message content
    keptMessages[msgIndex] = {
      ...keptMessages[msgIndex],
      content: newContent.trim(),
    };

    // Set messages to truncated version + new user message
    // We'll append a new user message with the edited content
    // so the AI can re-process it fresh
    setMessages(keptMessages);

    setIsProcessing(true);
    setError(null);

    try {
      // Build fresh history from the truncated messages
      // but replace the edited message content
      const history: ChatMessage[] = [];

      const currentPage = window.location.pathname;
      const lessonsContext = getLessonsContextString();

      history.push({
        role: 'system',
        content: assembleSystemPrompt({
          userName: user?.username || user?.firstName || undefined,
          currentPage,
          lessonsContext: lessonsContext || undefined,
        }),
      });

      // Add conversation history up to and including the edited message
      const addedToolCallIds = new Set<string>();
      for (const msg of keptMessages) {
        if (msg.role === 'user') {
          history.push({ role: 'user', content: msg.content });
        } else if (msg.role === 'assistant') {
          const assistantMsg: ChatMessage = {
            role: 'assistant',
            content: msg.content,
          };
          if (msg.toolCalls && msg.toolCalls.length > 0) {
            assistantMsg.tool_calls = msg.toolCalls;
          }
          history.push(assistantMsg);

          if (msg.toolResults && msg.toolResults.length > 0 && msg.toolCalls) {
            for (let i = 0; i < msg.toolResults.length; i++) {
              const tr = msg.toolResults[i];
              const tcId = msg.toolCalls[i]?.id || tr.toolName;
              if (!addedToolCallIds.has(tcId)) {
                addedToolCallIds.add(tcId);
                history.push({
                  role: 'tool',
                  content: JSON.stringify(tr.result),
                  tool_call_id: tcId,
                });
              }
            }
          }
        }
      }

      // Call AI with the edited history
      const response = await aiProvider.chat({
        messages: history,
        tools: allTools,
      }, token || undefined);

      const responseMsg = response.choices[0]?.message;

      const assistantMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: responseMsg.content || '',
        timestamp: new Date(),
        toolCalls: responseMsg.tool_calls,
      };

      setMessages(prev => [...prev, assistantMsg]);

      // If the AI wants to call tools, handle them
      if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
        try {
          await handleToolCallsInMessage(assistantMsg);
        } catch (toolError: any) {
          const recoveryMsg: ChatMessageDisplay = {
            id: generateId(),
            role: 'assistant',
            content: `I encountered an issue while processing the action (${toolError.message}). Please try again or let me know how I can help.`,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, recoveryMsg]);
        }
      }
    } catch (error: any) {
      const errorMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: `I apologize, but I encountered an error: ${error.message}\n\nPlease try again or rephrase your edited message. If the issue persists, check that everything is configured correctly.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, user, getLessonsContextString, handleToolCallsInMessage, token]);

  /**
   * Toggle chat open/closed
   */
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setPendingConfirmation(null);
    setEditingMessageId(null);
  }, []);

  return {
    // State
    messages,
    isProcessing,
    pendingConfirmation,
    isOpen,
    error,
    editingMessageId,

    // Actions
    sendMessage,
    clearChat,
    toggleOpen,
    openChat,
    closeChat,
    confirmAction,
    cancelAction,
    startEditingMessage,
    cancelEditing,
    editMessageAndContinue,
  };
}
